import { Platform, StyleSheet, type ViewStyle } from 'react-native';
import type { ChatListPalette } from './chatListPalette';
import { OVERLINE, TYPE, weight } from './typography';
import { RADIUS, SPACE } from './spacing';
import {
  CONTROL,
  SCREEN_PAD_H,
  controlChipS,
  controlGlyphText,
  controlIconBtn,
  controlPill,
  controlTextM,
  controlTextS,
} from './controlMetrics';

/**
 * Чем карточка списка отделяется от фона — по решению палитры (см. `itemSeparation`).
 *
 * <p>Светлая тема кладёт мягкую тень и обходится без обводки вовсе: белая карточка на
 * `#e7edf5` и так читается, а рамка поверх тени превращает ленту в таблицу. Тёмная тень
 * отбрасывать некуда, поэтому там остаётся рамка — но в полпикселя, а не в целый.
 *
 * <p>`boxShadow` для веба и `shadow*`/`elevation` для платформ: в RN 0.83 первый работает и
 * нативно, но `Platform.select` здесь уже принят в файле (см. `retentionTimerPressable`),
 * и ломать единообразие ради одной строки смысла нет.
 */
function itemSeparation(p: ChatListPalette): ViewStyle {
  if (p.itemSeparation === 'hairline') {
    return { borderWidth: StyleSheet.hairlineWidth, borderColor: p.itemBorder };
  }
  return {
    borderWidth: 0,
    ...Platform.select({
      web: { boxShadow: '0 1px 3px rgba(15, 23, 42, 0.10), 0 1px 2px rgba(15, 23, 42, 0.06)' },
      ios: {
        shadowColor: '#0f172a',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 3,
      },
      android: { elevation: 1 },
      default: {},
    }),
  };
}

/** Full ChatListScreen stylesheet from palette tokens. */
export function makeChatListStyles(p: ChatListPalette) {
  return StyleSheet.create({
    screenRoot: {
      flex: 1,
      backgroundColor: p.screenRoot,
      overflow: 'hidden',
    },
    aliveDot: {
      position: 'absolute',
      top: 6,
      left: 6,
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: p.aliveDot,
      zIndex: 9999,
    },
    bgImage: {
      position: 'absolute',
    },
    scrim: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: p.scrim,
    },
    content: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      width: '100%',
    },
    list: { flex: 1, backgroundColor: 'transparent' },
    /**
     * Плавающая шапка: заголовок, ряды кнопок, чипы фильтров и панель хирда — над списком,
     * а не перед ним. `zIndex` нужен и на вебе, и на Android: там порядок отрисовки решает
     * не порядок в разметке, а он.
     */
    floatingBar: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 2,
      // Скругление низа даёт понять, что панель лежит поверх, а не вырезана из экрана.
      borderBottomLeftRadius: RADIUS.lg,
      borderBottomRightRadius: RADIUS.lg,
      overflow: 'hidden',
    },
    chatControlsRow: {
      flexDirection: 'row',
      // `stretch`, а не `center`: если подпись фильтра всё-таки перенесётся на две строки,
      // подросший чип не станет выше соседей — ряд останется ровным.
      alignItems: 'stretch',
      gap: CONTROL.gapRow,
      paddingHorizontal: SCREEN_PAD_H,
      marginTop: SPACE.hair,
      marginBottom: SPACE.sm,
    },
    /**
     * Чип-фильтр — та же «пилюля», что и кнопки ряда выше: два ряда стоят в одной сетке, поэтому
     * ни высота, ни радиус, ни рамка у них не расходятся. А вот горизонтальные поля пилюли здесь
     * сняты: чип стоит на `flex: 1`, от паддинга его внешняя ширина не растёт — он только
     * отъедает место под подпись. На 360dp чипу достаётся (360 − 32 − 30) / 4 ≈ 74 px, и 20 px
     * полей оставили бы под «Discussions»/«Kanali» ~52 px: слово одиночное, переносить его
     * некуда, оно бы вылезло или обрезалось.
     */
    chatFilterChip: {
      ...controlPill,
      flex: 1,
      // На телефоне чипу достаётся ~74 px, а на окне десктопа — по 270: четыре кнопки-плиты
      // во всю ширину. Предел держит их пилюлями; на телефоне до него далеко.
      maxWidth: 140,
      paddingHorizontal: SPACE.hair,
      backgroundColor: p.chatFilterChip,
      borderColor: p.chatFilterChipBorder,
    },
    chatFilterChipActive: {
      backgroundColor: p.chatFilterChipActive,
      borderColor: p.chatFilterChipActiveBorder,
    },
    chatFilterChipText: {
      ...controlTextS,
      color: p.chatFilterChipText,
    },
    chatFilterChipTextActive: {
      color: p.chatFilterChipTextActive,
    },
    // Кнопка сортировки живёт в том же ряду и тем же чипом — разной у неё остаётся только
    // подпись, но не начертание.
    chatSortBtnText: {
      ...controlTextS,
      color: p.chatSortBtnText,
    },
    header: {
      paddingHorizontal: SCREEN_PAD_H,
      paddingBottom: SPACE.md,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: CONTROL.gapRow,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: CONTROL.gapRow,
      flexShrink: 0,
    },
    headerActionBtn: {
      ...controlIconBtn,
      backgroundColor: p.headerActionBtn,
      borderColor: p.headerActionBtnBorder,
    },
    headerActionIconImage: {
      width: CONTROL.iconM,
      height: CONTROL.iconM,
    },
    h1: {
      color: p.h1,
      ...TYPE.largeTitle,
      ...(p.headerTitleShadow
        ? Platform.select({
            web: { textShadow: '0px 0px 6px rgba(0,0,0,0.45)' },
            default: {
              textShadowColor: 'rgba(0,0,0,0.45)',
              textShadowRadius: 6,
            },
          }) ?? {}
        : {}),
    },
    /**
     * Имя владельца аккаунта в шапке — первая строка блока «кто я».
     *
     * <p>Ступень та же, что у имени в строке списка (`headline`): блок стоит прямо над лентой
     * и читается как её продолжение. До этого имя шло `footnote` цветом подписи — мельче и
     * бледнее превью чата под ним, из-за чего 44-пиксельная аватарка рядом выглядела подписанной
     * по случайности. Отступа сверху нет: он отбивал имя от заголовка «Чаты», а заголовок убран.
     */
    headerSelfName: {
      color: p.h1,
      ...TYPE.headline,
      ...(p.headerTitleShadow
        ? Platform.select({
            web: { textShadow: '0px 0px 6px rgba(0,0,0,0.45)' },
            default: {
              textShadowColor: 'rgba(0,0,0,0.45)',
              textShadowRadius: 6,
            },
          }) ?? {}
        : {}),
    },
    /** Строка статуса целиком: значок «не беспокоить» (если включено) и сам текст. */
    headerSelfStatusRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE.hair * 2 },
    /**
     * Перечёркнутый колокольчик рядом со статусом. Своя полоска, а не `notifyMutedSlashLine`:
     * та рассчитана на значок в строке чата (20 px) и поверх пятнадцатого вылезала бы за него.
     *
     * <p>Цвет — тот же, что у текста строки, а не тревожный `notifyMutedSlash`. В строке чата
     * красная черта отвечает на действие («заглушил») и стоит поверх яркого значка; здесь это
     * спокойное состояние, набранное в одну краску с подписью, — иначе в шапке появляется
     * красная отметина, которая выглядит как предупреждение о поломке.
     */
    headerSelfDndIcon: { width: 15, height: 15, alignItems: 'center', justifyContent: 'center' },
    headerSelfDndSlash: {
      position: 'absolute',
      width: 14,
      height: 1.8,
      borderRadius: RADIUS.pill,
      backgroundColor: p.h2,
      transform: [{ rotate: '-38deg' }],
    },
    /**
     * Вторая строка того же блока: статус, а без него — ник, а без ника — приглашение статус
     * поставить. Кегль и цвет достались ей от прежнего имени: роль подписи под аватаркой никуда
     * не делась, просто теперь её занимает строка, которой есть что сообщить.
     */
    headerSelfStatus: {
      color: p.h2,
      ...TYPE.footnote,
      ...(p.headerTitleShadow
        ? Platform.select({
            web: { textShadow: '0px 0px 4px rgba(0,0,0,0.4)' },
            default: {
              textShadowColor: 'rgba(0,0,0,0.4)',
              textShadowRadius: 4,
            },
          }) ?? {}
        : {}),
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: CONTROL.gapRow,
      paddingHorizontal: SCREEN_PAD_H,
      marginBottom: SPACE.sm,
    },
    /** Кнопка дружины, когда пришла входящая заявка: цвет из палитры, а не инлайном. */
    /** Счётчик входящих заявок в друзья на кнопке дружины (стори 5.7). */
    contactRequestBadge: {
      position: 'absolute',
      top: -4,
      right: -6,
      minWidth: 16,
      height: 16,
      paddingHorizontal: SPACE.xs,
      borderRadius: RADIUS.pill,
      backgroundColor: '#dc2626',
      alignItems: 'center',
      justifyContent: 'center',
    },
    contactRequestBadgeText: {
      color: '#fff',
      ...weight('micro', '800'),
    },
    druzhinaPanel: {
      marginHorizontal: SPACE.lg,
      marginBottom: SPACE.sm,
      borderRadius: RADIUS.lg,
      borderWidth: 1,
      borderColor: p.druzhinaPanelBorder,
      backgroundColor: p.druzhinaPanel,
      padding: SPACE.md,
      gap: SPACE.sm,
    },
    druzhinaTitle: { color: p.druzhinaTitle, ...TYPE.headline },
    druzhinaAdd: { color: p.druzhinaAdd, ...weight('footnote', '600') },
    /**
     * Компактный чип-тумблер (онлайн-фильтр дружины, реакции/комментарии групп и каналов).
     * Радиус 14 делал из него полную пилюлю рядом с прямоугольными кнопками — теперь радиус
     * общий: либо всё пилюли, либо всё скруглённые прямоугольники, смешивать нельзя.
     */
    druzhinaFilterChip: {
      ...controlChipS,
      backgroundColor: p.druzhinaFilterChip,
      borderColor: p.druzhinaFilterChipBorder,
    },
    druzhinaFilterChipActive: {
      backgroundColor: p.druzhinaFilterChipActive,
      borderColor: p.druzhinaFilterChipActiveBorder,
    },
    druzhinaFilterChipText: { ...controlTextS, color: p.druzhinaFilterChipText },
    druzhinaFilterChipTextActive: { color: p.druzhinaFilterChipTextActive },
    druzhinaHint: { color: p.druzhinaHint, ...TYPE.caption },
    druzhinaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: RADIUS.md,
      borderWidth: 1,
      borderColor: p.druzhinaRowBorder,
      backgroundColor: p.druzhinaRow,
      paddingVertical: SPACE.sm,
      paddingHorizontal: SPACE.md,
    },
    druzhinaName: { color: p.druzhinaName, ...weight('body', '600'), flex: 1, marginRight: SPACE.sm },
    actionIconImage: { width: 20, height: 20 },
    /** Строка листа действий, вызванного удержанием плашки: иконка слева, подпись рядом. */
    item: {
      marginHorizontal: SPACE.md,
      marginVertical: SPACE.xs,
      paddingVertical: SPACE.sm,
      paddingHorizontal: SPACE.sm,
      backgroundColor: p.item,
      borderRadius: RADIUS.md,
      ...itemSeparation(p),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: SPACE.sm,
    },
    /**
     * Строка чата, открытого в правой панели (двухпанельный режим, только веб). Кольцо внутрь,
     * а не рамка: рамка добавила бы строке ширины и сдвинула текст на два пикселя.
     */
    itemSelected: Platform.select<ViewStyle>({
      web: {
        boxShadow: `inset 0 0 0 2px ${p.chatFilterChipTextActive}, 0 1px 3px rgba(15, 23, 42, 0.10)`,
      },
      default: { borderWidth: 2, borderColor: p.chatFilterChipTextActive },
    }),
    itemAccent: {
      width: 3,
      alignSelf: 'stretch',
      borderRadius: 4,
      backgroundColor: p.itemAccentDirect,
    },
    itemAccentDirect: {
      backgroundColor: p.itemAccentDirect,
    },
    itemAccentGroup: {
      backgroundColor: p.itemAccentGroup,
    },
    itemAccentChannel: {
      backgroundColor: p.itemAccentChannel,
    },
    itemAccentSaved: {
      backgroundColor: p.itemAccentSaved,
    },
    /**
     * Закреплённая карточка «Избранного» (эпик 8, стори 8.2). Та же геометрия, что у обычной
     * строки, — она стоит первой в том же списке и не должна читаться чужеродной вставкой.
     */
    savedCardIconSlot: { width: 38, alignItems: 'center', justifyContent: 'center' },
    itemOpenArea: {
      flex: 1,
      minWidth: 0,
      borderRadius: RADIUS.md,
      paddingVertical: SPACE.xs,
      paddingHorizontal: SPACE.xs,
    },
    itemTitle: { color: p.itemTitle, ...TYPE.headline, flexShrink: 1 },
    itemTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: SPACE.sm,
    },
    presenceDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      borderWidth: 1,
    },
    presenceDotOnline: {
      backgroundColor: p.presenceDotOnline,
      borderColor: p.presenceDotOnlineBorder,
    },
    presenceDotOffline: {
      backgroundColor: p.presenceDotOffline,
      borderColor: p.presenceDotOfflineBorder,
    },
    groupOnlineBadge: {
      marginLeft: SPACE.sm,
      paddingHorizontal: SPACE.sm,
      height: 22,
      borderRadius: RADIUS.pill,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: p.groupOnlineBadge,
      borderWidth: 1,
      borderColor: p.groupOnlineBadgeBorder,
    },
    groupOnlineBadgeText: {
      color: p.groupOnlineBadgeText,
      ...weight('micro', '700'),
    },
    unreadBadge: {
      marginLeft: SPACE.sm,
      minWidth: 22,
      paddingHorizontal: SPACE.sm,
      height: 22,
      borderRadius: RADIUS.pill,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: p.unreadBadge,
      borderWidth: 1,
      borderColor: p.unreadBadgeBorder,
    },
    unreadBadgeText: {
      color: '#fff',
      ...weight('caption', '700'),
    },
    itemMeta: { color: p.itemMeta, marginTop: SPACE.hair, ...OVERLINE, textTransform: 'uppercase' },
    itemPreview: { color: p.itemPreview, ...TYPE.footnote, marginTop: SPACE.hair },
    itemPreviewRow: { marginTop: SPACE.hair, flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
    itemPreviewSticker: { width: 22, height: 22, marginTop: SPACE.hair },
    retentionTimerPressable: {
      alignSelf: 'center',
      justifyContent: 'center',
      minHeight: 34,
      paddingHorizontal: SPACE.md,
      paddingVertical: SPACE.xs,
      borderRadius: RADIUS.pill,
      backgroundColor: p.retentionTimerBg,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: p.retentionTimerBorder,
      ...Platform.select({
        web: {
          boxShadow: '0 2px 12px rgba(15, 23, 42, 0.12)',
          cursor: 'pointer',
        },
        ios: {
          shadowColor: '#0f172a',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.12,
          shadowRadius: 6,
        },
        android: { elevation: 2 },
        default: {},
      }),
    },
    retentionTimerText: {
      ...weight('caption', '800'),
      letterSpacing: 0.3,
      color: p.retentionTimerText,
      fontVariant: ['tabular-nums'],
    },
    tileActions: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'stretch',
      gap: SPACE.md,
      marginLeft: SPACE.md,
    },
    empty: {
      color: p.empty,
      textAlign: 'center',
      marginTop: SPACE.xxl,
      paddingHorizontal: SPACE.xl,
      ...(p.headerTitleShadow
        ? Platform.select({
            web: { textShadow: '0px 0px 6px rgba(0,0,0,0.45)' },
            default: {
              textShadowColor: 'rgba(0,0,0,0.45)',
              textShadowRadius: 6,
            },
          }) ?? {}
        : {}),
    },
    modalBg: {
      flex: 1,
      backgroundColor: p.modalBg,
      justifyContent: 'center',
      padding: SPACE.lg,
    },
    modalCard: { backgroundColor: p.modalCard, borderRadius: RADIUS.lg, padding: SPACE.lg, gap: SPACE.md },
    settingsCard: { gap: SPACE.sm },
    settingsSectionTitle: {
      color: p.settingsSectionTitle,
      ...weight('footnote', '700'),
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: SPACE.hair,
    },
    settingsFieldLabel: {
      color: p.settingsFieldLabel,
      ...weight('caption', '600'),
    },
    settingsInlineHint: {
      color: p.settingsInlineHint,
      ...TYPE.caption,
    },
    settingsSectionDivider: {
      height: 1,
      backgroundColor: p.settingsSectionDivider,
      marginVertical: SPACE.sm,
    },
    /**
     * Стори 18.1. Раздел настроек — замкнутая карточка, а не кусок общего потока, отчёркнутый
     * линией в 1 px. Разницы между «подпись поля» и «заголовок раздела» в один пункт кегля не
     * хватало: подписи, поля, тумблеры и кнопки шли одним ровным списком, и ни одна кнопка
     * «Сохранить» не выглядела принадлежащей своей форме — отсюда и «много кнопок, непонятно
     * какая за что».
     *
     * Рамка вместо заливки и никаких новых цветов в палитре: карточка настроек уже лежит на
     * `modalCard`, а вторая заливка поверх неё в светлых темах даёт грязь.
     */
    settingsGroupCard: {
      borderWidth: 1,
      borderColor: p.settingsSectionDivider,
      borderRadius: RADIUS.md,
      padding: SPACE.md,
      gap: SPACE.sm,
    },
    settingsGroupTitle: {
      color: p.settingsSectionTitle,
      ...weight('body', '700'),
      marginBottom: SPACE.hair,
    },
    /** Нажатая, но недоступная кнопка обязана выглядеть недоступной. */
    modalBtnDisabled: {
      opacity: 0.45,
    },
    /**
     * Высота в процентах, а не жёсткие 560 px: форма создания пользователя выше
     * этого лимита на телефоне, и список уже заведённых уезжал за край карточки
     * без возможности до него доскроллить — «в админке ничего нельзя сделать»
     * (стори 5.10). Содержимое лежит в ScrollView (bossCardScroll).
     */
    bossCard: { maxHeight: '90%' },
    bossCardScroll: { flexShrink: 1 },
    bossPasswordRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.sm,
    },
    bossPasswordInput: { flex: 1, minWidth: 0 },
    bossRegenBtn: {
      paddingVertical: SPACE.md,
      paddingHorizontal: SPACE.md,
      borderRadius: CONTROL.radius,
      backgroundColor: p.bossRegenBtn,
      flexShrink: 0,
    },
    bossRegenBtnText: { color: p.bossRegenBtnText, ...weight('caption', '600') },
    bossCopyBlock: { gap: SPACE.sm, marginTop: SPACE.xs },
    bossCopyBtn: { alignSelf: 'stretch' },
    bossList: { marginTop: SPACE.sm, gap: 0 },
    bossRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: SPACE.md,
      paddingHorizontal: SPACE.md,
      backgroundColor: p.bossRow,
      borderRadius: RADIUS.md,
      marginBottom: SPACE.sm,
      borderWidth: 1,
      borderColor: p.bossRowBorder,
      gap: SPACE.sm,
    },
    bossRowTitle: { color: p.bossRowTitle, ...weight('body', '600') },
    bossRowMeta: { color: p.bossRowMeta, ...TYPE.caption, marginTop: SPACE.hair },
    /**
     * Кнопка удаления — своя область нажатия, а не голый Text: на Android
     * попасть в 14-пиксельную надпись внутри строки списка почти невозможно.
     */
    bossDeleteBtn: { paddingVertical: SPACE.sm, paddingHorizontal: SPACE.sm, marginVertical: -8, flexShrink: 0 },
    bossDelete: { color: p.bossDelete, ...weight('body', '600') },
    bossRowOpen: { flex: 1, minWidth: 0 },
    /**
     * Подтверждение удаления живёт внутри модалки BOSS, а не отдельным <Modal>:
     * два одновременно видимых RN-Modal на Android — это два системных диалога,
     * и второй уезжает под первый. В вебе оба — просто div'ы, поэтому там
     * удаление работало, а на телефоне нажатие уходило в никуда.
     */
    bossConfirmOverlay: { position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, zIndex: 10 },
    dmCard: { maxHeight: 520 },
    /**
     * Список внутри листа. `flexGrow: 0` — чтобы короткий список не растягивал лист на весь
     * экран (лист теперь высотой по содержимому), `flexShrink: 1` — чтобы длинный уступил место
     * кнопкам под ним, когда лист упёрся в потолок. Потолок самого списка поднят с 280:
     * при высоте листа по содержимому лишние строки больше не оплачиваются пустотой.
     */
    dmList: { flexGrow: 0, flexShrink: 1, maxHeight: 360, marginTop: SPACE.xs },
    dmRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: SPACE.md,
      paddingHorizontal: SPACE.md,
      backgroundColor: p.dmRow,
      borderRadius: RADIUS.md,
      marginBottom: SPACE.sm,
      borderWidth: 1,
      borderColor: p.dmRowBorder,
    },
    dmRowName: { color: p.dmRowName, ...TYPE.headline },
    dmRowHint: { color: p.dmRowHint, ...TYPE.footnote },
    dmRowDisabled: { opacity: 0.45 },
    dmEmpty: { color: p.dmEmpty, textAlign: 'center', paddingVertical: SPACE.lg },
    dmErr: { color: p.dmErr, ...TYPE.footnote, marginTop: SPACE.xs },
    // Подтверждение сохранения (п. 6.5): берём тот же зелёный, что и индикатор присутствия,
    // чтобы не заводить ещё один цвет в трёх палитрах.
    settingsSavedNote: { color: p.presenceDotOnline, ...TYPE.footnote, marginTop: SPACE.xs },
    channelPermsRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' },
    /** Карточка статьи на витрине канала: её нажимают, чтобы прочитать до подписки. */
    channelPreviewCard: {
      gap: SPACE.xs,
      paddingVertical: SPACE.sm,
      paddingHorizontal: SPACE.sm,
      borderRadius: RADIUS.md,
      backgroundColor: p.channelDirRow,
      borderWidth: 1,
      borderColor: p.channelDirRowBorder,
    },
    channelPreviewCover: {
      width: '100%',
      height: 140,
      borderRadius: RADIUS.sm,
      marginBottom: SPACE.xs,
    },
    channelDirRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.sm,
      paddingVertical: SPACE.md,
      paddingHorizontal: SPACE.md,
      borderRadius: RADIUS.md,
      backgroundColor: p.channelDirRow,
      borderWidth: 1,
      borderColor: p.channelDirRowBorder,
      marginBottom: SPACE.sm,
    },
    channelDirDescription: { color: p.channelDirDescription, ...TYPE.footnote, marginTop: SPACE.hair },
    channelDirMeta: { color: p.channelDirMeta, ...TYPE.micro, marginTop: SPACE.hair },
    /**
     * Запись каталога каналов: сверху аватарка с названием, снизу — действия.
     *
     * <p>Раньше это был один горизонтальный ряд, и кнопки («Подписаться», «Управлять»,
     * «Поделиться») стояли справа столбиком. На телефоне столбик шириной 94 забирал у названия
     * почти треть строки — описание канала рвалось на четыре слова, — а сам ряд вырастал в
     * высоту на три кнопки. Каталог из пяти каналов выглядел как пять панелей управления.
     *
     * <p>Действия под текстом: названию достаётся вся ширина, кнопки встают в строку и
     * переносятся, только если их три и язык длинный.
     */
    channelDirCard: {
      gap: SPACE.sm,
      paddingVertical: SPACE.md,
      paddingHorizontal: SPACE.md,
      borderRadius: RADIUS.md,
      backgroundColor: p.channelDirRow,
      borderWidth: 1,
      borderColor: p.channelDirRowBorder,
      marginBottom: SPACE.sm,
    },
    channelDirCardTop: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
    channelDirActions: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACE.sm },
    /** Кнопки делят ширину поровну; `flexBasis` держит их в одной строке, пока она есть. */
    channelActionBtn: { flexGrow: 1, flexBasis: 96, alignItems: 'center' },
    gSelectedRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: SPACE.sm,
      paddingHorizontal: SPACE.md,
      backgroundColor: p.gSelectedRow,
      borderRadius: RADIUS.md,
      borderWidth: 1,
      borderColor: p.gSelectedRowBorder,
    },
    gSelectedName: { color: p.gSelectedName, ...weight('body', '600'), flex: 1, marginRight: SPACE.sm },
    gRemove: { color: p.gRemove, ...weight('body', '600') },
    modalTitle: { color: p.modalTitle, ...TYPE.title },
    input: {
      backgroundColor: p.input,
      borderRadius: CONTROL.radius,
      padding: SPACE.md,
      color: p.inputText,
      borderWidth: 1,
      borderColor: p.inputBorder,
    },
    hint: { color: p.hint, ...TYPE.caption },
    /**
     * Ряд кнопок в подвале модалки. `flexWrap` обязателен, а не «на всякий случай»: в окне
     * управления каналом их три с длинными русскими подписями («Отмена», «Удалить канал»,
     * «Сохранить канал»), и на телефоне сумма их ширин больше карточки. Без переноса
     * `justify-content: flex-end` выталкивает лишнее ЗА ЛЕВЫЙ край карточки — кнопки
     * оказываются на затемнении поверх списка чатов, ровно как на снимке владельца.
     */
    modalRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: CONTROL.gapRow,
      marginTop: SPACE.sm,
    },
    /**
     * Модальная кнопка была единственной во всём приложении без рамки — рядом с «×» и вкладками
     * она читалась элементом другого класса, и рамку ей дописывали инлайном по месту (кнопка
     * «Скопировать» в настройках). Теперь рамка системная, цвет — уже существующий
     * `smallBtnBorder`, новых токенов не заводим.
     */
    modalBtn: {
      ...controlPill,
      backgroundColor: p.modalBtn,
      borderColor: p.smallBtnBorder,
      // Одного `flexWrap` у ряда мало: даже вдвоём «Удалить канал» и «Сохранить канал» шире
      // карточки на телефоне. `flexShrink` разрешает подписи перенестись на вторую строку
      // внутри самой кнопки — высота у пилюли задана через `minHeight`, место для этого есть.
      flexShrink: 1,
      minWidth: 0,
    },
    // Рамка залитой кнопки — её собственный цвет: серый контур поверх синей/красной заливки
    // в светлых темах давал грязный ободок.
    modalPrimary: { backgroundColor: p.modalPrimary, borderColor: p.modalPrimary },
    modalDanger: { backgroundColor: p.modalDanger, borderColor: p.modalDanger },
    /**
     * Разрушающее действие ВТОРОГО плана: «удалить фото» рядом с «покинуть группу», «отписаться»
     * рядом с «удалить канал». Залитой красной кнопке на поверхности место одно — иначе оба
     * действия кричат одинаково громко, и то, которое не отменить, перестаёт выделяться.
     */
    modalDangerOutline: { borderColor: p.modalDanger },
    modalDangerOutlineText: { ...controlTextM, color: p.modalDangerText },
    modalBtnText: { ...controlTextM, color: p.modalBtnText },
    /**
     * Вкладки окна настроек — та же геометрия, что у чипов-фильтров списка чатов; отличаются
     * только раскладкой (пять штук в две строки) и тем, что нейтральный фон берётся от
     * модальной кнопки, на которой они лежат.
     */
    settingsTabChip: {
      ...controlPill,
      paddingHorizontal: CONTROL.padHS,
      backgroundColor: p.modalBtn,
      borderColor: p.smallBtnBorder,
    },
    settingsTabChipActive: {
      backgroundColor: p.chatFilterChipActive,
      borderColor: p.chatFilterChipActiveBorder,
    },
    settingsTabChipText: { ...controlTextS, color: p.modalBtnText, textAlign: 'center' },
    settingsTabChipTextActive: { color: p.chatFilterChipTextActive },
    /**
     * Строка участника в окнах группы и канала. Раньше оба окна рисовали её своим локальным
     * `StyleSheet` — отсюда и «настройки групповых чатов выглядят не как остальные настройки»:
     * рядом с `druzhinaRow` списка чатов это была строка из другого приложения (свои отступы,
     * своя рамка, свои кегли). Геометрия здесь та же, что у строки дружины; отличается только
     * `gap`, потому что слева стоит аватар, а не голый текст.
     */
    memberRow: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: CONTROL.radius,
      borderWidth: CONTROL.borderWidth,
      borderColor: p.druzhinaRowBorder,
      backgroundColor: p.druzhinaRow,
      paddingVertical: SPACE.sm,
      paddingHorizontal: SPACE.md,
      gap: CONTROL.gapIconText,
      marginBottom: SPACE.sm,
    },
    memberRowInfo: { flex: 1, minWidth: 0 },
    memberRowName: { color: p.druzhinaName, ...weight('body', '600') },
    memberRowHandle: { color: p.dmRowHint, ...TYPE.caption, marginTop: SPACE.hair },
    memberRowActions: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
    /**
     * Бейдж роли («Админ»). Был полупрозрачной заливкой акцента с ним же в тексте — по прямой
     * жалобе владельца «пометка админ сливается с текстом»: на светлой строке 13 % заливки не
     * отделяли бейдж от фона, а текст шёл тем же цветом, что и ссылка рядом. Теперь это ровно
     * активный чип-фильтр — единственная в приложении форма «выделенного состояния», контраст
     * которой уже выверен во всех пяти палитрах.
     */
    memberRoleBadge: {
      ...controlChipS,
      backgroundColor: p.chatFilterChipActive,
      borderColor: p.chatFilterChipActiveBorder,
    },
    memberRoleBadgeText: { ...controlTextS, color: p.chatFilterChipTextActive },
    /** Квадратная иконочная кнопка внутри модалки («×») — по геометрии кнопок шапки. */
    iconSquareBtn: {
      ...controlIconBtn,
      backgroundColor: p.modalBtn,
      borderColor: p.smallBtnBorder,
    },
    iconSquareBtnGlyph: { ...controlGlyphText, color: p.modalBtnText },
    /** Та же кнопка в разрушающем действии (исключить участника): цвет из `modalDanger`. */
    iconSquareBtnDanger: { borderColor: p.modalDanger },
    iconSquareBtnDangerGlyph: { ...controlGlyphText, color: p.modalDanger },

    // ——— Вариант A аудита 2026-10-04: «+», поиск, заявки, «Новое сообщение» ———

    /**
     * Поле поиска над списком чатов и на экране «Новое сообщение». Высота 44 — минимум зоны
     * нажатия; лупа внутри поля, а не отдельной кнопкой: искать начинают, просто нажав на поле.
     */
    searchBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.sm,
      minHeight: 44,
      marginHorizontal: SCREEN_PAD_H,
      marginBottom: SPACE.sm,
      paddingHorizontal: SPACE.md,
      borderRadius: CONTROL.radius,
      backgroundColor: p.input,
      borderWidth: 1,
      borderColor: p.inputBorder,
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      color: p.inputText,
      ...TYPE.body,
      paddingVertical: Platform.OS === 'web' ? SPACE.sm : SPACE.xs,
    },
    searchClearBtn: { width: 44, height: 44, marginRight: -SPACE.md, alignItems: 'center', justifyContent: 'center' },
    /** Плашка «Заявки в контакты: N» над списком — вместо лампочки на кнопке дружины (п. 5). */
    requestsBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.sm,
      minHeight: 44,
      marginHorizontal: SCREEN_PAD_H,
      marginBottom: SPACE.sm,
      paddingHorizontal: SPACE.md,
      borderRadius: CONTROL.radius,
      backgroundColor: p.chatFilterChipActive,
      borderWidth: 1,
      borderColor: p.chatFilterChipActiveBorder,
    },
    requestsBannerDot: { width: 8, height: 8, borderRadius: RADIUS.pill, backgroundColor: '#dc2626' },
    requestsBannerText: { flex: 1, color: p.chatFilterChipTextActive, ...weight('body', '600') },
    requestsBannerChevron: { color: p.chatFilterChipTextActive, ...weight('headline', '600') },
    /**
     * Плавающая «+» на Android (Material FAB, 56 dp): над нижней системной панелью, справа, где
     * её ищет большой палец. На iOS и в вебе вместо неё — карандаш в шапке.
     */
    fab: {
      position: 'absolute',
      right: SPACE.lg,
      width: 56,
      height: 56,
      borderRadius: RADIUS.lg,
      backgroundColor: p.modalPrimary,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3,
      ...Platform.select({
        web: { boxShadow: '0 3px 8px rgba(15, 23, 42, 0.3)' },
        ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.25, shadowRadius: 6 },
        android: { elevation: 6 },
        default: {},
      }),
    },
    /** Строка-ссылка под списком/в каталоге: «Открыть каталог каналов». */
    linkRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.md,
      minHeight: 48,
      marginHorizontal: SPACE.md,
      marginVertical: SPACE.xs,
      paddingHorizontal: SPACE.md,
      borderRadius: RADIUS.md,
      backgroundColor: p.item,
      ...itemSeparation(p),
    },
    linkRowText: { flex: 1, color: p.itemTitle, ...weight('body', '600') },
    /** Пустой список и ошибка загрузки — карточкой с действиями, а не одной строкой текста. */
    stateCard: {
      marginHorizontal: SPACE.md,
      marginTop: SPACE.lg,
      padding: SPACE.lg,
      gap: SPACE.md,
      borderRadius: RADIUS.lg,
      backgroundColor: p.item,
      ...itemSeparation(p),
    },
    stateCardTitle: { color: p.itemTitle, ...TYPE.headline, textAlign: 'center' },
    stateCardBody: { color: p.itemPreview, ...TYPE.footnote, textAlign: 'center' },
    stateCardActions: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: CONTROL.gapRow },
    /** Значок «без звука» рядом с названием чата. */
    mutedIcon: { marginLeft: SPACE.xs, opacity: 0.8 },
    /** Строка меню долгого нажатия / сортировки: вся строка — кнопка не ниже 48 dp. */
    menuRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.md,
      minHeight: 48,
      paddingHorizontal: SPACE.md,
      borderRadius: RADIUS.md,
      backgroundColor: p.requestRow,
      borderWidth: 1,
      borderColor: p.requestRowBorder,
    },
    menuRowText: { flex: 1, minWidth: 0, color: p.itemTitle, ...weight('body', '600') },
    menuRowDangerText: { color: p.modalDanger },
    menuRowCheck: { color: p.chatFilterChipTextActive, ...weight('body', '700') },
    menuHint: { color: p.hint, ...TYPE.caption, paddingHorizontal: SPACE.xs },
    menuTitleRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
    /** Галочка «удалить и у собеседника» в подтверждении удаления. */
    checkRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE.md, minHeight: 48 },
    checkBox: {
      width: 22,
      height: 22,
      borderRadius: RADIUS.sm,
      borderWidth: 2,
      borderColor: p.inputBorder,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkBoxOn: { backgroundColor: p.modalPrimary, borderColor: p.modalPrimary },
    checkRowText: { flex: 1, color: p.itemTitle, ...weight('body', '600') },

    /** Экран «Новое сообщение» и шаги новой группы — без обоев, на фоне экрана темы. */
    composeScreen: { flex: 1, backgroundColor: p.screenRoot },
    composeTop: { paddingTop: SPACE.md },
    composeListContent: { paddingBottom: SPACE.xxl },
    /** Строка действия («Новая группа», «Пригласить…»): значок в круге, подпись, без рамки. */
    composeAction: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.md,
      minHeight: 52,
      paddingHorizontal: SCREEN_PAD_H,
    },
    composeActionIcon: {
      width: 40,
      height: 40,
      borderRadius: RADIUS.pill,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: p.chatFilterChipActive,
    },
    // Без flex: подпись в колонке растягивалась на всю высоту строки и прилипала к верху,
    // мимо центра иконки (прогон 2026-10-05).
    composeActionText: { color: p.itemTitle, ...weight('body', '600') },
    composeActionHint: { color: p.hint, ...TYPE.caption },
    sectionHeader: {
      paddingHorizontal: SCREEN_PAD_H,
      paddingTop: SPACE.lg,
      paddingBottom: SPACE.xs,
      backgroundColor: p.screenRoot,
    },
    sectionHeaderText: { color: p.hint, ...OVERLINE, textTransform: 'uppercase' },
    sectionNote: { color: p.hint, ...TYPE.footnote, paddingHorizontal: SCREEN_PAD_H, paddingVertical: SPACE.sm },
    sectionError: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.md,
      paddingHorizontal: SCREEN_PAD_H,
      paddingVertical: SPACE.sm,
    },
    sectionErrorText: { flex: 1, color: p.dmErr, ...TYPE.footnote },
    /** Строка человека: аватар, имя и подпись, действие справа. Вся строка — 56 dp. */
    personRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.md,
      minHeight: 60,
      paddingHorizontal: SCREEN_PAD_H,
      paddingVertical: SPACE.xs,
    },
    personMain: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: SPACE.md, minHeight: 52 },
    personText: { flex: 1, minWidth: 0 },
    personName: { color: p.itemTitle, ...weight('body', '600') },
    personMeta: { color: p.itemPreview, ...TYPE.caption, marginTop: SPACE.hair },
    personMetaOnline: { color: p.presenceDotOnline },
    personAvatarDot: {
      position: 'absolute',
      right: -1,
      bottom: -1,
      width: 12,
      height: 12,
      borderRadius: RADIUS.sm,
      borderWidth: 2,
      borderColor: p.screenRoot,
      backgroundColor: p.presenceDotOnline,
    },
    /** Главная кнопка строки («Принять», «Добавить»): залитая пилюля не ниже 44 dp. */
    pillPrimary: {
      ...controlPill,
      minHeight: 44,
      paddingHorizontal: SPACE.lg,
      backgroundColor: p.modalPrimary,
      borderColor: p.modalPrimary,
    },
    pillPrimaryText: { ...controlTextM, color: '#fff' },
    pillSecondary: {
      ...controlPill,
      minHeight: 44,
      paddingHorizontal: SPACE.lg,
      backgroundColor: p.modalBtn,
      borderColor: p.smallBtnBorder,
    },
    pillSecondaryText: { ...controlTextM, color: p.modalBtnText },
    pillMutedText: { color: p.hint, ...weight('footnote', '600') },
    /** Квадрат 44×44 под «✕»: отклонить заявку, убрать участника. */
    iconBtn44: { width: 44, height: 44, borderRadius: RADIUS.pill, alignItems: 'center', justifyContent: 'center' },
    /** Чипы выбранных участников новой группы — сверху, нажатие убирает. */
    chipsRow: { paddingHorizontal: SCREEN_PAD_H, paddingBottom: SPACE.sm, gap: SPACE.sm },
    memberChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACE.xs,
      minHeight: 36,
      paddingLeft: SPACE.xs,
      paddingRight: SPACE.sm,
      borderRadius: RADIUS.pill,
      backgroundColor: p.chatFilterChipActive,
      borderWidth: 1,
      borderColor: p.chatFilterChipActiveBorder,
    },
    memberChipText: { color: p.chatFilterChipTextActive, ...weight('footnote', '600'), maxWidth: 120 },
    /** Кружок-флажок выбора участника. */
    pickCircle: {
      width: 26,
      height: 26,
      borderRadius: RADIUS.pill,
      borderWidth: 2,
      borderColor: p.inputBorder,
      alignItems: 'center',
      justifyContent: 'center',
    },
    pickCircleOn: { backgroundColor: p.modalPrimary, borderColor: p.modalPrimary },
    /** Нижняя панель шага («Далее», «Создать»): над системной панелью, во всю ширину. */
    stepBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: CONTROL.gapRow,
      paddingHorizontal: SCREEN_PAD_H,
      paddingTop: SPACE.sm,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.settingsSectionDivider,
      backgroundColor: p.screenRoot,
    },
    stepBarInfo: { flex: 1, color: p.hint, ...TYPE.footnote },
    stepPrimaryDisabled: { opacity: 0.45 },
    formBody: { padding: SCREEN_PAD_H, gap: SPACE.md },
    formLabel: { color: p.itemTitle, ...weight('body', '600') },
    /** QR и ссылка в листе приглашения. */
    qrBox: { alignSelf: 'center', padding: SPACE.sm, borderRadius: RADIUS.md, backgroundColor: '#ffffff' },
    inviteLinkText: { color: p.itemTitle, ...TYPE.footnote, textAlign: 'center' },
  });
}

export type ChatListStyles = ReturnType<typeof makeChatListStyles>;
